import React, { useState, useRef } from "react";
import { Form, Input as AntInput, Button, notification, Modal, Spin, Typography } from "antd";
import { useQuery, useQueryClient } from "react-query";
import styled from "styled-components";
import { AuthLayout } from "../organisms/auth-layout/AuthLayout";
import {
  getCompany,
  getCompanyBranches,
  editCompany,
  editCompanyBranch,
  checkSlugAvailability,
  getCurrentUser,
} from "../../service/client";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import URLInstructionImg from "../../images/URL_Instruction.png";
import { useTourContext } from "../../contexts/tour-context/useTourContext";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

const libraries: ("places" | "geometry" | "drawing")[] = ["geometry", "places", "drawing"];
const GOOGLE_MAPS_API_KEY = "AIzaSyA3lh6-T9qUYkdo46jFI1jaNQ_z2JNdCEs";

// Reuse styled components from RegisterFormNew
const StyledTitle = styled(Typography.Title)`
  &.ant-typography {
    margin-bottom: 12px !important;
    color: #FEFFFC !important;
    font-weight: 500;
    text-align: center;
    font-size: clamp(2rem, 4vw, 3rem) !important;
  }
`;

const StyledSubtitle = styled(Text)`
  display: block;
  margin-bottom: 48px;
  color: #FEFFFC;
  font-size: 18px;
  text-align: center;
  opacity: 0.9;
`;

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 24px;

  .ant-form-item-label > label {
    color: #FEFFFC !important;
    font-size: 14px;
    font-weight: 500;
    height: auto;
  }

  .ant-input-affix-wrapper,
  .ant-input:not(.ant-input-affix-wrapper > input) {
    height: 52px;
    background-color: #263e44 !important;
    border: 1px solid rgba(255, 255, 255, 0.4) !important;
    border-radius: 8px !important;
    color: #FEFFFC !important;
    padding: 0 12px !important;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;

    &:hover, &:focus, &-focused {
      border-color: #EBFED1 !important;
      background-color: #2c474d !important;
      box-shadow: none !important;
    }

    input {
      background-color: transparent !important;
      color: #FEFFFC !important;
      border: none !important;
      height: 100% !important;
      padding: 0 !important;

      &::placeholder {
        color: rgba(255, 255, 255, 0.5) !important;
      }
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.5) !important;
    }
  }

  &.ant-form-item-has-error {
    .ant-input-affix-wrapper,
    .ant-input:not(.ant-input-affix-wrapper > input) {
      border-color: #ff4d4f !important;

      &:hover, &:focus, &-focused {
        border-color: #ff4d4f !important;
      }
    }
  }

  .ant-form-item-explain-error {
    font-size: 12px;
    margin-top: 4px;
    color: #ff4d4f;
  }
`;

const StyledButton = styled(Button)``;

const FieldHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const FieldLabel = styled.div`
  color: #FEFFFC;
  font-size: 16px;
  font-weight: 600;
`;

const LearnMoreLink = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  text-decoration: underline;
  transition: color 0.2s;

  &:hover {
    color: #EBFED1;
  }
`;

const SlugPrefix = styled.span`
  color: #FEFFFC;
  font-size: 16px;
  white-space: nowrap;
  margin-right: 2px;
`;

const AddressInputWrapper = styled.div`
  position: relative;

  .pac-container {
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    margin-top: 4px;
  }
`;

const HelperText = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
  margin-top: 8px;
  line-height: 1.5;
`;

const HelpModalContent = styled.div`
  .help-section {
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .help-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .help-description {
    color: var(--color-text-secondary);
    font-size: 14px;
    line-height: 1.6;
  }

  .help-image {
    width: 100%;
    border-radius: 8px;
    border: 1px solid var(--color-border);
    margin-bottom: 16px;
  }
`;

type HelpTopic = "slug" | "name" | "address";

const HELP_CONTENT: Record<HelpTopic, { titleKey: string; icon: string; descriptionKey: string; showImage: boolean }> = {
  slug: {
    titleKey: "helpSlugTitle",
    icon: "\uD83D\uDD17",
    descriptionKey: "helpSlugDescription",
    showImage: true,
  },
  name: {
    titleKey: "helpNameTitle",
    icon: "\uD83D\uDFE8",
    descriptionKey: "helpNameDescription",
    showImage: true,
  },
  address: {
    titleKey: "helpAddressTitle",
    icon: "\uD83D\uDCCD",
    descriptionKey: "helpAddressDescription",
    showImage: true,
  },
};

interface OnboardingProps {
  onComplete: () => void;
}

export const Onboarding: React.FC<OnboardingProps> = ({ onComplete }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { startTour } = useTourContext();
  const [helpModalTopic, setHelpModalTopic] = useState<HelpTopic | null>(null);
  const [addressValue, setAddressValue] = useState<string>("");
  const [selectedCoordinates, setSelectedCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddressValid, setIsAddressValid] = useState(false);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const { data: company } = useQuery(["/company"], getCompany);
  const { data: branches } = useQuery(["/company-branches"], getCompanyBranches);
  const branch = branches?.[0];

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry?.location) {
        const components = place.address_components || [];
        const hasStreetNumber = components.some((c: google.maps.GeocoderAddressComponent) => c.types.includes("street_number"));
        const hasPremise = components.some((c: google.maps.GeocoderAddressComponent) => c.types.includes("premise"));
        const hasRoute = components.some((c: google.maps.GeocoderAddressComponent) => c.types.includes("route"));
        if ((!hasStreetNumber && !hasPremise) || (hasRoute && !hasStreetNumber)) {
          setAddressValue("");
          setIsAddressValid(false);
          setSelectedCoordinates(null);
          notification.warning({
            message: t("onboardingPage.missingBuildingNumberTitle"),
            description: t("onboardingPage.missingBuildingNumberDescription"),
            placement: "bottomRight",
          });
          return;
        }
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setSelectedCoordinates({ lat, lng });
        setAddressValue(place.formatted_address || "");
        setIsAddressValid(true);
      }
    }
  };

  const handleSubmit = async (values: any) => {
    if (!company?.id || !branch?.id) return;

    setIsSubmitting(true);
    try {
      const slug = values.slug.trim().toLowerCase();

      // Validate slug availability
      const slugCheck = await checkSlugAvailability(slug);
      if (!slugCheck?.available) {
        form.setFields([
          {
            name: "slug",
            errors: [slugCheck?.reason || t("onboardingPage.slugTakenError")],
          },
        ]);
        setIsSubmitting(false);
        return;
      }

      // Update company slug
      await editCompany({
        id: company.id,
        data: {
          slug,
          name: values.name.trim(),
          adress: addressValue.split(',')[0].trim(),
          city: addressValue.split(',').pop()?.trim(),
        },
      });

      // Validate address was selected from autocomplete
      if (!selectedCoordinates) {
        notification.error({
          message: t("onboardingPage.selectAddressTitle"),
          description: t("onboardingPage.selectAddressDescription"),
          placement: "bottomRight",
        });
        setIsSubmitting(false);
        return;
      }

      // Update branch name and address
      const branchData: any = {
        name: values.name.trim(),
        geographicPoint: {
          lat: selectedCoordinates.lat,
          lng: selectedCoordinates.lng,
        },
        formattedAddress: addressValue,
      };

      await editCompanyBranch({
        id: branch.id,
        data: branchData,
      });

      // Invalidate all relevant queries
      await queryClient.invalidateQueries(["/company"]);
      await queryClient.invalidateQueries(["/company-branches"]);

      onComplete();
      // Uruchom tour po wyrenderowaniu głównego widoku (tylko jeśli nie był jeszcze ukończony)
      const user = await getCurrentUser();
      if (!user?.tourProducts) {
        setTimeout(() => startTour(), 800);
      }
    } catch (error: any) {
      notification.error({
        message: t("onboardingPage.errorTitle"),
        description: error?.response?.data?.error || t("onboardingPage.saveErrorDescription"),
        placement: "bottomRight",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Pre-fill existing values (slug always empty — user must choose their own)
  const currentName = branch?.name || "";

  return (
    <>
      <AuthLayout maxWidth={700}>
        <StyledTitle level={1}>{t("onboardingPage.mainTitle")}</StyledTitle>
        <StyledSubtitle>
          {t("onboardingPage.subtitle")}
        </StyledSubtitle>

        <Form form={form} onFinish={handleSubmit} layout="vertical" requiredMark={false}>
          {/* Slug field */}
          <FieldHeader>
            <FieldLabel>{t("onboardingPage.slugFieldLabel")}</FieldLabel>
            <LearnMoreLink onClick={() => setHelpModalTopic("slug")}>
              {t("onboardingPage.learnMore")}
            </LearnMoreLink>
          </FieldHeader>
          <StyledFormItem
            name="slug"
            initialValue=""
            rules={[
              { required: true, message: t("onboardingPage.slugRequired") },
              {
                pattern: /^[a-z0-9-]+$/,
                message: t("onboardingPage.slugPattern"),
              },
              { min: 3, message: t("onboardingPage.slugMin") },
              { max: 50, message: t("onboardingPage.slugMax") },
            ]}
            normalize={(value: string) => {
              if (!value) return value;
              return value
                .toLowerCase()
                .replace(/[ąàáâã]/g, "a")
                .replace(/ć/g, "c")
                .replace(/[ęèéê]/g, "e")
                .replace(/ł/g, "l")
                .replace(/ń/g, "n")
                .replace(/[óòôõ]/g, "o")
                .replace(/ś/g, "s")
                .replace(/[źż]/g, "z")
                .replace(/\s+/g, "-")
                .replace(/[^a-z0-9-]/g, "");
            }}
          >
            <AntInput
              prefix={<SlugPrefix>proste.menu/</SlugPrefix>}
              placeholder="pizzeria-bozenka"
              size="large"
            />
          </StyledFormItem>

          {/* Name field */}
          <FieldHeader>
            <FieldLabel>{t("onboardingPage.nameFieldLabel")}</FieldLabel>
            <LearnMoreLink onClick={() => setHelpModalTopic("name")}>
              {t("onboardingPage.learnMore")}
            </LearnMoreLink>
          </FieldHeader>
          <StyledFormItem
            name="name"
            initialValue={currentName}
            rules={[
              { required: true, message: t("onboardingPage.nameRequired") },
              { min: 2, message: t("onboardingPage.nameMin") },
              { max: 100, message: t("onboardingPage.nameMax") },
            ]}
          >
            <AntInput placeholder={t("onboardingPage.namePlaceholder")} size="large" />
          </StyledFormItem>

          {/* Address field */}
          <FieldHeader>
            <FieldLabel>{t("onboardingPage.addressFieldLabel")}</FieldLabel>
            <LearnMoreLink onClick={() => setHelpModalTopic("address")}>
              {t("onboardingPage.learnMore")}
            </LearnMoreLink>
          </FieldHeader>
          <StyledFormItem>
            <AddressInputWrapper>
              {isLoaded ? (
                <Autocomplete
                  onLoad={(autocomplete) => {
                    autocomplete.setFields(["place_id", "geometry", "formatted_address", "address_components"]);
                    autocompleteRef.current = autocomplete;
                  }}
                  onPlaceChanged={handlePlaceChanged}
                  options={{
                    componentRestrictions: { country: "pl" },
                    types: ["address"],
                  }}
                >
                  <AntInput
                    placeholder={t("onboardingPage.addressPlaceholder")}
                    size="large"
                    value={addressValue}
                    onChange={(e) => {
                      setAddressValue(e.target.value);
                      setIsAddressValid(false);
                      setSelectedCoordinates(null);
                    }}
                    status={addressValue && !isAddressValid ? "error" : ""}
                    style={{
                      height: 52,
                      backgroundColor: "#263e44",
                      border: addressValue && !isAddressValid ? "1px solid #ff4d4f" : "1px solid rgba(255, 255, 255, 0.4)",
                      borderRadius: 8,
                      color: "#FEFFFC",
                      padding: "0 12px",
                      opacity: 1,
                      cursor: 'text',
                    }}
                  />
                </Autocomplete>
              ) : (
                <AntInput
                  placeholder={t("onboardingPage.loading")}
                  prefix={<Spin size="small" />}
                  disabled
                  size="large"
                />
              )}
            </AddressInputWrapper>
            {addressValue && !isAddressValid && (
              <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                {t("onboardingPage.addressSelectHint")}
              </div>
            )}
          </StyledFormItem>
          <HelperText>
            {t("onboardingPage.addressHelperText")}
          </HelperText>

          <Form.Item style={{ marginBottom: 0, marginTop: 32 }}>
            <StyledButton
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              block
              disabled={!selectedCoordinates || !isAddressValid}
            >
              {isSubmitting ? t("onboardingPage.creatingMenu") : t("onboardingPage.createMenu")}
            </StyledButton>
          </Form.Item>
        </Form>
      </AuthLayout>

      {/* Help Modal */}
      <Modal
        open={helpModalTopic !== null}
        onCancel={() => setHelpModalTopic(null)}
        footer={null}
        title={
          <span style={{ fontSize: 18, fontWeight: 600 }}>
            {t("onboardingPage.helpModalTitle")}
          </span>
        }
        width={500}
      >
        {helpModalTopic && (
          <HelpModalContent>
            {/* Browser preview image */}
            {HELP_CONTENT[helpModalTopic].showImage && (
              <img src={URLInstructionImg} alt={t("onboardingPage.previewAlt")} className="help-image" />
            )}

            {/* All help sections */}
            {(Object.keys(HELP_CONTENT) as HelpTopic[]).map((topic) => {
              const content = HELP_CONTENT[topic];
              return (
                <div className="help-section" key={topic}>
                  <div className="help-title">
                    {content.icon} {t(`onboardingPage.${content.titleKey}`)}
                  </div>
                  <div className="help-description">{t(`onboardingPage.${content.descriptionKey}`)}</div>
                </div>
              );
            })}
          </HelpModalContent>
        )}
      </Modal>
    </>
  );
};
